// import { Layout } from './components/layout';
import {
  Assessment as AssessmentIcon,
  Extension as ExtensionIcon,
  FileUploadOutlined,
  History as HistoryIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  MovieCreation as MovieCreationIcon,
  PieChart as PieChartIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import { CssBaseline, GlobalStyles, Box } from '@mui/material';
import { Authenticated, Refine } from '@refinedev/core';
import { RefineKbarProvider, RefineKbar } from '@refinedev/kbar';
import { RefineSnackbarProvider, ThemedLayout, useNotificationProvider } from '@refinedev/mui';
import routerProvider, { CatchAllNavigate, NavigateToResource } from '@refinedev/react-router';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';

import AttackIQTitleComponent from './components/AttackIQTitleComponent';
import Header from './components/Header/Header';
import { ColorModeContextProvider } from './contexts';
import authProvider from './providers/authProviders';
import { loggedInRoutes, routeConfig } from './routeConfig';
import httpClient from './utils/axios';
import drfDataProvider from './utils/drfDataProvider';

const API_URL = '/v1';

const defaultDataProvider = drfDataProvider(API_URL, httpClient);

const SidebarIcon = ({ children }: { children?: React.ReactNode }) => (
  <Box
    sx={{
      color: 'gray',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {children}
  </Box>
);

const NoIcon = () => null;

const OffLayoutArea: React.FC = () => {
  return <RefineKbar />;
};

function App() {
  return (
    <BrowserRouter>
      <ColorModeContextProvider>
        <RefineKbarProvider>
          <RefineSnackbarProvider>
            <CssBaseline />
            <GlobalStyles styles={{ html: { WebkitFontSmoothing: 'auto' } }} />
            <Refine
              notificationProvider={useNotificationProvider}
              routerProvider={routerProvider}
              authProvider={authProvider}
              options={{
                disableTelemetry: true
              }}
              resources={[
                // ---- MODULES SECTION ----
                {
                  meta: {
                    icon: (
                      <SidebarIcon>
                        <ViewModuleIcon />
                      </SidebarIcon>
                    )
                  },
                  name: '_modules'
                },
                {
                  meta: {
                    parent: '_modules',
                    icon: <NoIcon />,
                    canDelete: true
                  },
                  name: 'modules',
                  create: routeConfig.modules.create.url,
                  edit: routeConfig.modules.edit.url,
                  list: routeConfig.modules.list.url,
                  show: routeConfig.modules.show.url
                },
                {
                  meta: {
                    parent: '_modules',
                    icon: <NoIcon />
                  },
                  name: 'module_contents',
                  list: routeConfig.module_contents.list.url,
                  create: routeConfig.module_contents.create.url
                },
                // ---- ASSESSMENTS SECTION ----
                {
                  meta: {
                    icon: (
                      <SidebarIcon>
                        <AssessmentIcon />
                      </SidebarIcon>
                    )
                  },
                  name: 'assessments'
                },
                {
                  meta: {
                    parent: 'assessments',
                    icon: <NoIcon />
                  },
                  name: 'assessment_templates',
                  list: routeConfig.assessment_templates.list.url,
                  edit: routeConfig.assessment_templates.edit.url
                },
                // ---- REPORTS SECTION ----
                {
                  meta: {
                    icon: (
                      <SidebarIcon>
                        <PieChartIcon />
                      </SidebarIcon>
                    )
                  },
                  name: 'reports'
                },
                {
                  meta: {
                    parent: 'reports',
                    icon: <NoIcon />
                  },
                  name: 'report_types',
                  edit: routeConfig.report_types.edit.url,
                  create: routeConfig.report_types.create.url,
                  list: routeConfig.report_types.list.url
                },
                // ---- SCENARIOS SECTION ----
                {
                  name: 'content',
                  meta: {
                    label: 'Scenarios',
                    icon: (
                      <SidebarIcon>
                        <MovieCreationIcon />
                      </SidebarIcon>
                    )
                  }
                },
                {
                  meta: {
                    parent: 'content',
                    icon: <NoIcon />
                  },
                  name: 'scenarios',
                  create: routeConfig.scenarios.create.url,
                  list: routeConfig.scenarios.list.url,
                  edit: routeConfig.scenarios.edit.url
                },
                {
                  meta: {
                    parent: 'content',
                    icon: <NoIcon />
                  },
                  name: 'mitigations',
                  list: routeConfig.mitigations.list.url,
                  edit: routeConfig.mitigations.edit.url,
                  create: routeConfig.mitigations.create.url
                },
                {
                  meta: {
                    parent: 'content',
                    icon: <NoIcon />
                  },
                  name: 'unified_mitigations',
                  list: routeConfig.unified_mitigations.list.url,
                  edit: routeConfig.unified_mitigations.edit.url,
                  create: routeConfig.unified_mitigations.create.url
                },
                {
                  name: 'scenario_templates',
                  meta: {
                    label: 'Templates',
                    parent: 'content',
                    icon: <NoIcon />
                  },
                  list: routeConfig.scenario_templates.list.url,
                  edit: routeConfig.scenario_templates.edit.url
                },
                {
                  meta: {
                    parent: 'content',
                    icon: <NoIcon />
                  },
                  name: 'phases',
                  list: routeConfig.phases.list.url,
                  edit: routeConfig.phases.edit.url
                },
                {
                  meta: {
                    parent: 'content',
                    icon: <NoIcon />
                  },
                  name: 'cloud_providers',
                  list: routeConfig.cloud_providers.list.url,
                  edit: routeConfig.cloud_providers.edit.url,
                  create: routeConfig.cloud_providers.create.url
                },
                // ---- INTEGRATIONS SECTION ----
                {
                  meta: {
                    icon: (
                      <SidebarIcon>
                        <ExtensionIcon />
                      </SidebarIcon>
                    )
                  },
                  name: 'integrations'
                },
                {
                  meta: {
                    parent: 'integrations',
                    icon: <NoIcon />
                  },
                  name: 'connectors',
                  list: routeConfig.connectors.list.url,
                  create: routeConfig.connectors.create.url,
                  edit: routeConfig.connectors.edit.url
                },
                {
                  meta: {
                    parent: 'integrations',
                    icon: <NoIcon />
                  },
                  name: 'vendors',
                  list: routeConfig.vendors.list.url,
                  create: routeConfig.vendors.create.url,
                  edit: routeConfig.vendors.edit.url
                },
                {
                  meta: {
                    parent: 'integrations',
                    icon: <NoIcon />
                  },
                  name: 'vendor_products',
                  list: routeConfig.vendor_products.list.url,
                  create: routeConfig.vendor_products.create.url,
                  edit: routeConfig.vendor_products.edit.url
                },
                {
                  name: 'parameter_modeler',
                  list: routeConfig.parameter_modeler.list.url,
                  meta: {
                    label: 'Parameter modeler (legacy)',
                    parent: 'integrations',
                    icon: <NoIcon />
                  }
                },
                // ---- TAGGING SECTION ----
                {
                  name: 'tagging',
                  meta: {
                    label: 'Tagging',
                    icon: (
                      <SidebarIcon>
                        <LocalOfferIcon />
                      </SidebarIcon>
                    )
                  }
                },
                {
                  meta: {
                    icon: <NoIcon />,
                    parent: 'tagging'
                  },
                  name: 'tags',
                  list: routeConfig.tags.list.url,
                  edit: routeConfig.tags.edit.url,
                  create: routeConfig.tags.create.url
                },
                {
                  name: 'tag_sets',
                  meta: {
                    label: 'Tagsets',
                    icon: <NoIcon />,
                    parent: 'tagging'
                  },
                  list: routeConfig.tag_sets.list.url,
                  edit: routeConfig.tag_sets.edit.url,
                  create: routeConfig.tag_sets.create.url
                },
                {
                  name: 'tagged_items',
                  meta: {
                    label: 'Tagged Items',
                    icon: <NoIcon />,
                    parent: 'tagging'
                  },
                  list: routeConfig.tagged_items.list.url,
                  create: routeConfig.tagged_items.create.url
                },
                // ---- FLEX PACKAGES SECTION ----
                {
                  name: 'flex',
                  meta: {
                    label: 'Flex',
                    icon: (
                      <SidebarIcon>
                        <InventoryIcon />
                      </SidebarIcon>
                    )
                  }
                },
                {
                  name: 'flex_packages',
                  meta: {
                    label: 'Flex Packages',
                    icon: <NoIcon />,
                    parent: 'flex',
                    canDelete: true
                  },
                  list: routeConfig.flex_packages.list.url,
                  create: routeConfig.flex_packages.create.url,
                  edit: routeConfig.flex_packages.edit.url
                },
                // ---- AUDIT SECTION ----
                {
                  name: 'history',
                  list: routeConfig.history.list.url,
                  meta: {
                    label: 'History',
                    icon: (
                      <SidebarIcon>
                        <HistoryIcon />
                      </SidebarIcon>
                    )
                  }
                },
                // ---- NEWS SECTION ----
                {
                  meta: {
                    icon: (
                      <SidebarIcon>
                        <ViewModuleIcon />
                      </SidebarIcon>
                    )
                  },
                  name: '_news'
                },
                {
                  name: 'whats_new',
                  meta: {
                    label: "What's new",
                    parent: '_news',
                    icon: <NoIcon />,
                    canDelete: true
                  },
                  create: routeConfig.whats_new.create.url,
                  edit: routeConfig.whats_new.edit.url,
                  list: routeConfig.whats_new.list.url,
                  show: routeConfig.whats_new.show.url
                },
                {
                  name: 'threat_informed_defense',
                  meta: {
                    label: 'Threat Informed Defense',
                    parent: '_news',
                    icon: <NoIcon />,
                    canDelete: true
                  },
                  create: routeConfig.threat_informed_defense.create.url,
                  edit: routeConfig.threat_informed_defense.edit.url,
                  list: routeConfig.threat_informed_defense.list.url,
                  show: routeConfig.threat_informed_defense.show.url
                },
                {
                  meta: {
                    icon: (
                      <SidebarIcon>
                        <FileUploadOutlined />
                      </SidebarIcon>
                    )
                  },
                  name: '_files'
                },
                {
                  name: 'ready_documentation',
                  meta: {
                    label: 'Ready Docs',
                    parent: '_files',
                    icon: <NoIcon />,
                    canDelete: true
                  },
                  list: routeConfig.ready_documentation.list.url
                }
              ]}
              dataProvider={defaultDataProvider}
            >
              <Routes>
                <Route
                  element={
                    <Authenticated key="authenticated-inner" fallback={<CatchAllNavigate to="/login" />}>
                      <ThemedLayout
                        Header={() => <Header />}
                        OffLayoutArea={() => <OffLayoutArea />}
                        Title={() => <AttackIQTitleComponent collapsed />}
                      >
                        <Outlet />
                      </ThemedLayout>
                    </Authenticated>
                  }
                >
                  <Route index element={<NavigateToResource resource="modules" />} />
                  {loggedInRoutes()}
                </Route>
                <Route
                  element={
                    <Authenticated key="authenticated-outer" fallback={<Outlet />}>
                      <NavigateToResource />
                    </Authenticated>
                  }
                >
                  <Route path="/login" />
                </Route>
              </Routes>
            </Refine>
          </RefineSnackbarProvider>
        </RefineKbarProvider>
      </ColorModeContextProvider>
    </BrowserRouter>
  );
}

export default App;
